import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Upload, X, CheckCircle2, Loader2, PhoneCall,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import SeoHead from "../components/SeoHead";
import { PARTNER_BENEFITS, whatsappLink } from "../data/content";
import Icon from "../components/Icon";
import { LOCALITIES } from "../data/services";
import { useServices } from "../context/ServicesContext";
import { api, uploadFile, formatApiErrorDetail } from "../lib/api";

const STEP_LABELS = ["Profile", "Areas", "Verify", "Payout"];
const EXPERIENCE = ["0-1 years", "1-3 years", "3-5 years", "5-10 years", "10+ years"];

export default function PartnerRegisterPage() {
  const navigate = useNavigate();
  const { services } = useServices();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);

  const [form, setForm] = useState({
    name: "", mobile: "", email: "",
    service_category: "", skills: "", experience: "",
    service_areas: [], bank_upi: "", kyc: [],
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArea = (name) =>
    setForm((f) => ({ ...f, service_areas: f.service_areas.includes(name) ? f.service_areas.filter((a) => a !== name) : [...f.service_areas, name] }));

  const canNext = useMemo(() => {
    if (step === 0) return form.name.trim() && /^[0-9+\-\s]{10,15}$/.test(form.mobile) && form.service_category && form.experience;
    if (step === 1) return form.service_areas.length > 0;
    if (step === 2) return form.kyc.length > 0;
    if (step === 3) return form.bank_upi.trim().length > 3;
    return false;
  }, [step, form]);

  const handleKyc = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await uploadFile(file);
        setForm((f) => ({ ...f, kyc: [...f.kyc, { id: res.id, filename: res.filename }] }));
      }
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/partners", {
        name: form.name, mobile: form.mobile, email: form.email,
        service_category: form.service_category, skills: form.skills, experience: form.experience,
        service_areas: form.service_areas, bank_upi: form.bank_upi,
        kyc_doc_ids: form.kyc.map((k) => k.id),
      });
      setDone(data.partner);
      toast.success("Application submitted!");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <SeoHead title="Partner Application Submitted - ApnaFix" />
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center af-fade-up" data-testid="partner-confirmation">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 mx-auto"><CheckCircle2 className="w-9 h-9" /></span>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">Welcome aboard, {done.name.split(" ")[0]}!</h1>
          <p className="mt-2 text-slate-600">Your application for <span className="font-semibold text-navy">{done.service_category}</span> is submitted for verification. Our team will review your details and get in touch shortly.</p>
          <div className="mt-6 text-left bg-slate-50 rounded-2xl p-5 space-y-2 text-sm">
            <p className="flex justify-between"><span className="text-slate-500">Partner ID</span><span className="font-mono-num font-semibold text-slate-900">{done.id.slice(0, 8).toUpperCase()}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Trade</span><span className="font-medium text-slate-900">{done.service_category}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-medium text-amber-600">Pending verification</span></p>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a href={whatsappLink(`Hi ApnaFix, I submitted my partner application (ID ${done.id.slice(0,8).toUpperCase()}).`)} target="_blank" rel="noreferrer"
               className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 px-5"><PhoneCall className="w-4 h-4" /> Chat on WhatsApp</a>
            <Button variant="outline" onClick={() => navigate("/")} className="h-11 px-5 font-semibold">Back to Home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SeoHead title="Join as a Partner - ApnaFix" description="Register as an ApnaFix service partner. Get local customer leads, manage jobs and grow earnings." />
      {/* Hero strip */}
      <div className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white mb-3"><ArrowLeft className="w-4 h-4" /> Back</Link>
          <h1 className="font-display text-2xl sm:text-4xl font-extrabold tracking-tight">Grow Your Business With ApnaFix</h1>
          <p className="mt-2 text-slate-300 max-w-xl">Get more local customers, manage your jobs and grow your earnings — all from one platform.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {PARTNER_BENEFITS.map((b) => (
              <span key={b.title} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/10 px-3 py-1 text-xs"><Icon name={b.icon} className="w-3.5 h-3.5 text-orange-300" /> {b.title}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Stepper */}
        <div className="flex items-center gap-1 sm:gap-2">
          {STEP_LABELS.map((lbl, i) => (
            <div key={lbl} className="flex-1 flex items-center gap-1 sm:gap-2">
              <div className={`flex items-center gap-2 ${i <= step ? "text-navy" : "text-slate-400"}`}>
                <span className={`grid place-items-center w-7 h-7 rounded-full text-xs font-bold ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-400"}`}>{i < step ? <Check className="w-4 h-4" /> : i + 1}</span>
                <span className="hidden sm:inline text-xs font-semibold">{lbl}</span>
              </div>
              {i < STEP_LABELS.length - 1 && <div className={`flex-1 h-0.5 rounded ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-8 min-h-[340px]">
          {step === 0 && (
            <div className="af-fade-up space-y-4">
              <h2 className="font-display font-bold text-lg text-slate-900">Your details & primary trade</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sm">Full name</Label><Input data-testid="partner-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11" placeholder="Your name" /></div>
                <div><Label className="text-sm">Mobile number</Label><Input data-testid="partner-mobile-input" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="mt-1.5 h-11" placeholder="10-digit mobile" /></div>
              </div>
              <div><Label className="text-sm">Email (optional)</Label><Input data-testid="partner-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11" placeholder="you@email.com" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Primary service category</Label>
                  <Select value={form.service_category} onValueChange={(v) => set("service_category", v)}>
                    <SelectTrigger data-testid="partner-category-select" className="mt-1.5 h-11"><SelectValue placeholder="Select trade" /></SelectTrigger>
                    <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Experience</Label>
                  <Select value={form.experience} onValueChange={(v) => set("experience", v)}>
                    <SelectTrigger data-testid="partner-experience-select" className="mt-1.5 h-11"><SelectValue placeholder="Years" /></SelectTrigger>
                    <SelectContent>{EXPERIENCE.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="text-sm">Skills (optional)</Label><Textarea data-testid="partner-skills-input" value={form.skills} onChange={(e) => set("skills", e.target.value)} className="mt-1.5" placeholder="e.g. Split & window AC, inverter AC, gas charging" /></div>
            </div>
          )}

          {step === 1 && (
            <div className="af-fade-up">
              <h2 className="font-display font-bold text-lg text-slate-900 mb-1">Where do you want to work?</h2>
              <p className="text-sm text-slate-500 mb-4">Select the localities you can serve.</p>
              <div className="grid grid-cols-2 gap-3">
                {LOCALITIES.map((l) => {
                  const active = form.service_areas.includes(l.name);
                  return (
                    <button key={l.id} data-testid={`partner-area-${l.slug}`} onClick={() => toggleArea(l.name)}
                            className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${active ? "border-brand-orange bg-orange-50 ring-2 ring-orange-200" : "border-slate-200 hover:border-orange-300"}`}>
                      <span className={`grid place-items-center w-5 h-5 rounded border shrink-0 ${active ? "bg-brand-orange border-brand-orange text-white" : "border-slate-300 bg-white"}`}>{active && <Check className="w-3.5 h-3.5" />}</span>
                      <div><p className="font-semibold text-slate-900">{l.name}</p><p className="text-xs text-slate-500">{l.societies}</p></div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="af-fade-up space-y-4">
              <h2 className="font-display font-bold text-lg text-slate-900">ID & skill verification</h2>
              <p className="text-sm text-slate-500">Upload your Aadhaar / ID proof (and any skill certificate). We verify every partner before activation.</p>
              <div className="flex flex-wrap gap-3">
                {form.kyc.map((k) => (
                  <div key={k.id} className="relative w-24 h-24 rounded-xl bg-slate-100 border border-slate-200 grid place-items-center text-[10px] text-slate-500 p-2 text-center overflow-hidden">
                    <span className="line-clamp-3">{k.filename}</span>
                    <button onClick={() => set("kyc", form.kyc.filter((x) => x.id !== k.id))} className="absolute -top-1.5 -right-1.5 w-5 h-5 grid place-items-center rounded-full bg-red-500 text-white"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label data-testid="partner-kyc-upload" className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 grid place-items-center cursor-pointer hover:border-brand-orange text-slate-400 hover:text-brand-orange">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleKyc} />
                </label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="af-fade-up space-y-4">
              <h2 className="font-display font-bold text-lg text-slate-900">Payout details</h2>
              <p className="text-sm text-slate-500">Add your UPI ID or bank account for instant payouts.</p>
              <div><Label className="text-sm">UPI ID / Bank account</Label><Input data-testid="partner-bank-input" value={form.bank_upi} onChange={(e) => set("bank_upi", e.target.value)} className="mt-1.5 h-11" placeholder="name@upi or account number" /></div>
              <p className="text-xs text-slate-400">Your financial details are stored securely and used only for payouts.</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="font-semibold"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          {step < 3 ? (
            <Button data-testid="partner-next-btn" disabled={!canNext} onClick={() => setStep((s) => s + 1)} className="bg-brand-orange hover:bg-orange-600 text-white font-semibold px-6">Continue <ArrowRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button data-testid="partner-register-submit-btn" disabled={!canNext || submitting} onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Submitting...</> : <>Submit for Verification <Check className="w-4 h-4 ml-1" /></>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
